import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Axios, { markPageUnloading } from '@/shared/api/axios';
import { persistor } from '@/shared/store/store';
import { selectIsIdolLocked } from '@/shared/store/slices/idolSlice';
import { setCurrentIdol } from '@/shared/store/slices/userSlice';
import { useSignout } from '@/hooks/useSignout';
import { useSwitchToCreatedIdol } from '@/hooks/useSwitchToCreatedIdol';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import IdolPickList from '@/components/ui/IdolPickList';
import SuspensionAppeal from '@/components/shared/SuspensionAppeal';
import IdolNickname from '@/features/auth/components/IdolNickname';

/**
 * 偶像停權鎖定 modal（全域，掛在 App）。
 *
 * selectIsIdolLocked 為 true 時顯示，無法關閉，只允許：
 * - 切換到未停權的偶像（有可用偶像時）
 * - 創建偶像（沒有可用偶像時，按鈕 → 暱稱表單，同註冊流程）
 * - 登出
 * 切換或創建偶像後 idolId 改變，鎖定自動解除、modal 關閉。
 */
function IdolSuspendedModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isIdolLocked = useSelector(selectIsIdolLocked);
  const idolList = useSelector((state) => state.idol.list);
  const signout = useSignout();
  const switchToCreatedIdol = useSwitchToCreatedIdol();

  const modalRef = useRef(null);
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const idolSwitchingRef = useRef(false);

  const hasAvailableIdol = idolList.some((idol) => !idol.idol_is_suspended);

  useEffect(() => {
    if (isIdolLocked) {
      modalRef.current.show();
    } else {
      modalRef.current.hide();
      setIsCreating(false);
    }
  }, [isIdolLocked]);

  const handleError = (error, label) => {
    // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
    if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
      setCurrentStatusCode(error.httpErrorCode);
      statusModalRef.current?.show();
    } else if (!error.httpErrorCode) {
      console.error(label, error);
    }
  };

  // 切換偶像：與 navbar 相同，switch-check 成功後整頁導向該偶像頁
  const handlePickIdol = async (idol) => {
    // 切換進行中忽略再次點擊，避免並行請求互相覆蓋
    if (idolSwitchingRef.current) return;
    idolSwitchingRef.current = true;
    try {
      const res = await Axios.post('/idol/switch-check', { idol_id: idol.idol_id });
      if (res.data.status_code === 'SYSTEM_1000') {
        dispatch(setCurrentIdol(idol));
        // redux-persist 是非同步寫入，整頁跳轉前必須先 flush，否則重載後會 rehydrate 回停權偶像
        await persistor.flush();
        // 即將整頁跳轉，不解鎖
        markPageUnloading();
        window.location.href = `/idol/${idol.idol_uuid}`;
        return;
      }
      setCurrentStatusCode(res.data.status_code);
      statusModalRef.current?.show();
    } catch (error) {
      handleError(error, 'Switch idol error:');
    }
    idolSwitchingRef.current = false;
  };

  // 建立偶像後切換：失敗時偶像已建立，回到列表讓使用者直接點選切換，避免在表單重複建立
  const handleIdolCreated = async (res) => {
    try {
      const statusCode = await switchToCreatedIdol(res);
      if (statusCode === 'SYSTEM_1000') return;
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (statusCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      handleError(error, 'Switch to created idol error:');
    }
    setIsCreating(false);
  };

  const handleSignout = async () => {
    try {
      const statusCode = await signout();
      if (statusCode !== 'SYSTEM_1000') {
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      handleError(error, 'Signout error:');
    }
  };

  return (
    <>
      <MessageModal ref={modalRef} status="warning" size="md" locked>
        {isCreating ? (
          <>
            <IdolNickname onResponse={handleIdolCreated} />
            <Button variant="link" className="mt-2" onClick={() => setIsCreating(false)}>
              {t('back')}
            </Button>
          </>
        ) : (
          <>
            <h5 className="mb-2">{t('idol_suspended_title')}</h5>
            <p className="mb-4">{t(hasAvailableIdol ? 'idol_suspended_desc_switch' : 'idol_suspended_desc_create')}</p>
            <IdolPickList idols={idolList} onPick={handlePickIdol} />
            {!hasAvailableIdol && (
              <Button className="btn btn-primaryF w-100 mt-4" onClick={() => setIsCreating(true)}>
                <FontAwesomeIcon icon={['fas', 'plus']} className="me-2" />
                {t('create_idol')}
              </Button>
            )}
            <Button className="btn btn-neutral w-100 mt-2" onClick={handleSignout}>
              <FontAwesomeIcon icon={['fas', 'right-from-bracket']} className="me-2" />
              {t('signout')}
            </Button>
            <SuspensionAppeal className="mt-4" />
          </>
        )}
      </MessageModal>
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
}

export default IdolSuspendedModal;
